import { motion } from 'framer-motion';
import { cx } from '@twind/core';
import useModule from 'app/hooks/useModule';
import { ChatModuleType } from 'app/copied/models/ChatModules';
import TailorPrivacyBanner from 'app/modules/tailor/components/TailorPrivacyBanner';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import { useLocation, useNavigate } from 'react-router-dom';
import CustomScrollbar from 'app/components/CustomScrollbar';
import useOpenOnScroll from 'app/hooks/useOpenOnScroll';
import { useRef, useEffect, useState } from 'react';
import { rootContainer, shadowRoot } from 'app/shadowRoot';
import { useToast } from 'app/context/ToastContext';
import { FeedbackSentiment, FeedbackType, UserQueryType } from 'app/hooks/fetch/useFeedbackAnalytics';
import useIsIntegratedView from 'app/hooks/useIsIntegratedView';
import { ensureElementVisible } from 'app/utils/ensureElementVisible';

import Header from '../../../components/Header';
import {
  ChatItem,
  ChatItemFeedback,
  ChatItemForm,
  ChatItemFormType,
  ChatItemKBEntry,
  ChatItemMessage,
  ChatItemMessageType,
  ChatItemSourceType,
  ChatItemType,
  KBEntryViewType,
} from '../../../services/chatBot';
import ChatItemFactory from '../components/chat-items/ChatItemFactory';
import Typing from '../../../components/Typing';
import BaseChatItem from '../components/chat-items/BaseChatItem';
import LiveChatPrompt from '../../live-chat/components/LiveChatPrompt';
import useVirtualAssistant from '../hooks/useVirtualAssistant';
import useVirtualAssistantAvatar from '../hooks/useVirtualAssistantAvatar';
import { VirtualAssistantApp } from '../VirtualAssistantApp';
import FeedbackDrawer from '../components/chat-items/FeedbackDrawer';

const containerVariants = {
  hidden: {
    opacity: 0,
    originX: 0,
    originY: 'top',
  },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const isContactForm = (item: ChatItem) => {
  return item.type === ChatItemType.FORM && (item as ChatItemForm).form_type === ChatItemFormType.CONTACT_FORM;
};

export default function VirtualAssistant() {
  const location = useLocation();
  const isIntegratedView = useIsIntegratedView();
  const { data: helpCenter } = useFetchHelpCenter();
  const vaModule = useModule(ChatModuleType.VirtualAssistant);
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useOpenOnScroll(scrollContainerRef);
  const { showToast } = useToast();
  const [isFeedbackDrawerOpen, setIsFeedbackDrawerOpen] = useState(false);
  const [feedbackChatItem, setFeedbackChatItem] = useState<ChatItemFeedback | undefined>(undefined);
  const thankYouMessage = helpCenter?.customization?.fe_config?.feedback?.thankYouMessage;

  const {
    chatItems,
    isTyping,
    typingAreaAvailable,
    userMessage,
    isLoadingVirtualAssistant,
    userResponsePresent,
    visibleChatItems,
    chatContainerRef,
    answerResponse,
    setUserMessage,
    handleAnswer,
    handleDismiss,
    handleMessage,
    handleContentClick,
    handleDisplayFormItem,
    showTypingArea,
    resetScrollToBottomAfterInit,
    lastInitItemId,
  } = useVirtualAssistant();

  const avatarURL = useVirtualAssistantAvatar();

  const previousChatItemsRef = useRef(visibleChatItems);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.focus();
    }
  }, [chatContainerRef]);

  useEffect(() => {
    // Handle scroll to bottom after initChatBot
    if (lastInitItemId && chatContainerRef.current) {
      const lastInitItem = visibleChatItems.find((item) => item.uuid === lastInitItemId);
      if (lastInitItem?.visible) {
        const scrollElement = chatContainerRef.current;
        setTimeout(() => {
          if (isIntegratedView) {
            rootContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }

          scrollElement.scrollTo({
            top: scrollElement.scrollHeight,
            behavior: 'smooth',
          });
          resetScrollToBottomAfterInit();
        }, 100);
        return;
      }
    }

    const newMessages = visibleChatItems.filter((item) => {
      const isMessage = [ChatItemType.MESSAGE, ChatItemType.KB_ENTRY].includes(item.type);
      const isNew = !previousChatItemsRef.current.some((prevItem) => prevItem.uuid === item.uuid);

      return (isMessage || isContactForm(item)) && isNew;
    });

    const patientMessages = visibleChatItems.filter(
      (item) =>
        [ChatItemSourceType.PATIENT, ChatItemSourceType.PATIENT_CHOICE].includes(item.source) &&
        item.type === ChatItemType.MESSAGE
    );
    const latestPatientMessage = patientMessages[patientMessages.length - 1];
    const mostRecentMessage = newMessages[newMessages.length - 1];

    if (mostRecentMessage?.errorMessage) {
      const element = shadowRoot.getElementById(mostRecentMessage.uuid);
      if (element) {
        isIntegratedView
          ? ensureElementVisible(chatContainerRef.current, element)
          : element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (newMessages.length > 0 && latestPatientMessage) {
      const element = shadowRoot.getElementById(latestPatientMessage.uuid);
      if (element) {
        setTimeout(
          () => {
            isIntegratedView
              ? ensureElementVisible(chatContainerRef.current, element)
              : element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
          newMessages.some(isContactForm) ? 750 : 0
        );
      }
    }

    previousChatItemsRef.current = visibleChatItems;
  }, [visibleChatItems, chatContainerRef, resetScrollToBottomAfterInit, lastInitItemId, isIntegratedView]);

  const handleBackClick = () => {
    navigate('/tailor', { state: { isBack: true } });
  };

  // Keyboard navigation for accessibility
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'End':
          // Jump to latest message
          event.preventDefault();
          if (visibleChatItems.length > 0) {
            const latestMessage = visibleChatItems[visibleChatItems.length - 1];
            const element = shadowRoot.getElementById(latestMessage.uuid);
            if (element instanceof HTMLElement) {
              element.focus();
              isIntegratedView
                ? ensureElementVisible(chatContainerRef.current, element)
                : element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          break;
        case 'Home':
          // Jump to first message
          event.preventDefault();
          if (visibleChatItems.length > 0) {
            const firstMessage = visibleChatItems[0];
            const element = shadowRoot.getElementById(firstMessage.uuid);
            if (element instanceof HTMLElement) {
              element.focus();
              isIntegratedView
                ? ensureElementVisible(chatContainerRef.current, element)
                : element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
          break;
      }
    }
  };

  const showFeedbackToast = (message?: string) => {
    if (message) {
      showToast(message);
    }
  };

  return (
    <VirtualAssistantApp>
      <div
        id="ostro-va-container"
        className="flex grow flex-col h-0 relative overflow-hidden"
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        <div className="px-7 py-5 relative">
          <Header
            onClickBack={handleBackClick}
            showBackButton={!vaModule?.va_as_landing_page_enabled}
          />
        </div>
        <CustomScrollbar
          id="ostro-va-chat-container"
          containerRef={chatContainerRef}
          className="px-7 pt-5"
          alignToBottom
          tabIndex={0}
          aria-live="polite"
          role="log"
          aria-label="Virtual Assistant Conversation"
          aria-relevant="additions text"
        >
          {visibleChatItems.map((item, i) => {
            const prevItem = visibleChatItems[i - 1];
            const nextItem = visibleChatItems[i + 1];

            const hideAvatar = (() => {
              if (
                item.type === ChatItemType.MESSAGE &&
                (item as ChatItemMessage).message_type === ChatItemMessageType.MESSAGE_WITH_AVATAR
              ) {
                return false;
              }

              return (
                i > 0 &&
                prevItem.type !== ChatItemType.FORM &&
                prevItem.source !== ChatItemSourceType.PATIENT &&
                prevItem.source !== ChatItemSourceType.PATIENT_CHOICE
              );
            })();

            const isSourceLink =
              item.type === ChatItemType.KB_ENTRY && (item as ChatItemKBEntry).view_type === KBEntryViewType.SOURCE;
            const nextIsSourceLink =
              nextItem?.type === ChatItemType.KB_ENTRY &&
              (nextItem as ChatItemKBEntry).view_type === KBEntryViewType.SOURCE;

            return (
              <motion.div
                id={item.uuid}
                initial="hidden"
                animate="visible"
                variants={containerVariants}
                key={item.uuid}
              >
                <ChatItemFactory
                  shouldAnimate={i === visibleChatItems.length - 1}
                  className={cx(
                    i > 0 && !userResponsePresent ? 'flex-col [&>div:first-child]:hidden' : '',
                    i === 0 && !userResponsePresent ? 'flex-col' : '',
                    i === 0 && userResponsePresent ? '[&_h1]:text-sm' : '',
                    isSourceLink && nextIsSourceLink ? 'mb-1' : ''
                  )}
                  hideAvatar={hideAvatar}
                  answerItem={
                    item.type === ChatItemType.FORM && answerResponse?.form?.ref_id === (item as ChatItemForm).ref_id
                      ? answerResponse.form
                      : undefined
                  }
                  chatItem={item}
                  onAnswer={handleAnswer}
                  onDismiss={handleDismiss}
                  onClickContent={handleContentClick}
                  onDisplayFormItem={handleDisplayFormItem}
                  previousUserMessage={
                    (
                      visibleChatItems
                        .slice(0, i)
                        .reverse()
                        .find(
                          (prevItem) =>
                            prevItem.type === ChatItemType.MESSAGE && prevItem.source === ChatItemSourceType.PATIENT
                        ) as ChatItemMessage
                    )?.message
                  }
                  onFeedback={(feedbackSentiment) => {
                    if (feedbackSentiment === FeedbackSentiment.POSITIVE) {
                      showFeedbackToast(thankYouMessage);
                    } else {
                      setFeedbackChatItem(item as ChatItemFeedback);
                      setIsFeedbackDrawerOpen(true);
                    }
                  }}
                />
              </motion.div>
            );
          })}
          {(isTyping || isLoadingVirtualAssistant) && (
            <BaseChatItem
              avatarURL={avatarURL}
              className={!userResponsePresent ? '[&>div:first-child]:hidden' : ''}
              showAvatar={chatItems.length <= 1}
              isUserMessage={false}
              hideMessageOwnership
            >
              <Typing />
            </BaseChatItem>
          )}
        </CustomScrollbar>
        {isFeedbackDrawerOpen && feedbackChatItem && (
          <FeedbackDrawer
            isOpen={isFeedbackDrawerOpen}
            onClose={() => {
              setIsFeedbackDrawerOpen(false);
              showFeedbackToast(thankYouMessage);
            }}
            userQuery={feedbackChatItem.queryTyped}
            userQueryType={UserQueryType.AiChat}
            resultType={feedbackChatItem.resultType}
            feedbackType={FeedbackType.Response}
            secondaryFeedbackOptions={helpCenter?.customization?.fe_config?.feedback?.secondaryFeedbackOptions || []}
            secondaryFeedbackHeader={helpCenter?.customization?.fe_config?.feedback?.secondaryFeedbackHeader || ''}
            otherFieldEnabled={helpCenter?.customization?.fe_config?.feedback?.otherFieldEnabled || false}
          />
        )}
        {typingAreaAvailable && showTypingArea && (
          <LiveChatPrompt
            message={userMessage}
            disabled={false}
            onChange={setUserMessage}
            onSend={handleMessage}
          />
        )}
        {(vaModule?.va_as_landing_page_enabled || location.state?.source === 'nonWidget') && (
          <div className="shrink-0">
            <TailorPrivacyBanner footerText={helpCenter?.footer.lines} />
          </div>
        )}
      </div>
    </VirtualAssistantApp>
  );
}
